import type {
  Exam,
  ExamDetail,
  ExamListResponse,
  CreateExamRequest,
  UpdateExamRequest,
  ApprovedQuestionsResponse,
  ApprovedQuestionDetail,
  AutoFillRequest,
  AutoComposePreview,
  DocumentWithQuestions,
  QuestionSort,
} from '../types/composer';
import { apiClient } from '../api/apiClient';

/**
 * Read the filename a download should be saved under from Content-Disposition.
 *
 * The backend sends two parameters (RFC 6266): an ASCII-transliterated
 * `filename` for old clients, and `filename*` carrying the real name
 * UTF-8 percent-encoded. Preferring `filename*` is what keeps characters
 * the ASCII fallback can't represent — an em dash is dropped there, and
 * umlauts are transliterated to their base letter (`ü` → `u`) — intact.
 *
 * Path separators are stripped so a crafted header cannot suggest a name
 * that escapes the download directory.
 */
export function parseContentDispositionFilename(
  contentDisposition: string | undefined,
  fallback: string
): string {
  const sanitize = (name: string): string =>
    name.replace(/[/\\]/g, '_').trim();

  if (contentDisposition) {
    const encoded = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (encoded) {
      try {
        const decoded = sanitize(decodeURIComponent(encoded[1]));
        if (decoded) return decoded;
      } catch {
        // Malformed percent-encoding — fall through to the plain filename.
      }
    }

    const plain = contentDisposition.match(/filename="(.+)"/);
    if (plain) {
      const name = sanitize(plain[1]);
      if (name) return name;
    }
  }

  return fallback;
}

export class ComposerService {
  static async listExams(params?: {
    status?: string;
    search?: string;
    include_archived?: boolean;
    archived_only?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ExamListResponse> {
    const response = await apiClient.get('/api/v1/exams/', { params });
    return response.data;
  }

  static async getExam(examId: number): Promise<ExamDetail> {
    const response = await apiClient.get(`/api/v1/exams/${examId}`);
    return response.data;
  }

  static async createExam(data: CreateExamRequest): Promise<Exam> {
    const response = await apiClient.post('/api/v1/exams/', data);
    return response.data;
  }

  static async updateExam(examId: number, data: UpdateExamRequest): Promise<Exam> {
    const response = await apiClient.put(`/api/v1/exams/${examId}`, data);
    return response.data;
  }

  /**
   * Reassign (or clear) the grading scheme of an exam via the dedicated
   * PATCH endpoint. Unlike `updateExam` (PUT, draft-only) this works on
   * finalized/exported exams — reassigning the noten-skala is metadata-only.
   * `grading_scheme_id: null` clears it so the export falls back to the
   * institution default. `updated_at` is required by the backend for
   * optimistic locking (mismatch → 409).
   */
  static async updateExamGradingScheme(
    examId: number,
    data: { grading_scheme_id: number | null; updated_at: string },
  ): Promise<Exam> {
    const response = await apiClient.patch(
      `/api/v1/exams/${examId}/grading-scheme`,
      data,
    );
    return response.data;
  }

  static async deleteExam(examId: number): Promise<void> {
    await apiClient.delete(`/api/v1/exams/${examId}`);
  }

  // TF-398: archive (allowed in any status) / restore.
  static async archiveExam(examId: number, reason?: string): Promise<Exam> {
    const response = await apiClient.post(`/api/v1/exams/${examId}/archive`, {
      reason: reason ?? null,
    });
    return response.data;
  }

  static async restoreExam(examId: number): Promise<Exam> {
    const response = await apiClient.post(`/api/v1/exams/${examId}/restore`);
    return response.data;
  }

  static async addQuestions(examId: number, questionIds: number[]): Promise<ExamDetail> {
    const response = await apiClient.post(`/api/v1/exams/${examId}/questions`, {
      question_ids: questionIds,
    });
    return response.data;
  }

  static async updateExamQuestion(
    examId: number,
    eqId: number,
    data: { points?: number; section?: string }
  ): Promise<ExamDetail> {
    const response = await apiClient.put(`/api/v1/exams/${examId}/questions/${eqId}`, data);
    return response.data;
  }

  static async removeExamQuestion(examId: number, eqId: number): Promise<ExamDetail> {
    const response = await apiClient.delete(`/api/v1/exams/${examId}/questions/${eqId}`);
    return response.data;
  }

  static async reorderQuestions(
    examId: number,
    order: { id: number; position: number }[]
  ): Promise<ExamDetail> {
    const response = await apiClient.post(`/api/v1/exams/${examId}/reorder`, { order });
    return response.data;
  }

  static async autoFill(
    examId: number,
    request: AutoFillRequest
  ): Promise<ExamDetail | AutoComposePreview> {
    const response = await apiClient.post(`/api/v1/exams/${examId}/auto-fill`, request);
    return response.data;
  }

  static async finalizeExam(examId: number): Promise<Exam> {
    const response = await apiClient.post(`/api/v1/exams/${examId}/finalize`);
    return response.data;
  }

  static async unfinalizeExam(examId: number): Promise<Exam> {
    const response = await apiClient.post(`/api/v1/exams/${examId}/unfinalize`);
    return response.data;
  }

  static async listApprovedQuestions(params?: {
    topic?: string;
    difficulty?: string;
    bloom_level?: number;
    question_type?: string;
    // TF-406: Fachfilter-Facetten + Sortierung
    ln_level?: number;
    competency_id?: number;
    quality_tier?: string;
    unused?: boolean;
    sort?: QuestionSort;
    search?: string;
    tag_ids?: number[];
    document_ids?: number[];
    limit?: number;
    offset?: number;
  }): Promise<ApprovedQuestionsResponse> {
    const { tag_ids, document_ids, unused, sort, ...rest } = params ?? {};
    const response = await apiClient.get('/api/v1/exams/approved-questions', {
      params: {
        ...rest,
        ...(tag_ids?.length ? { tag_ids: tag_ids.join(',') } : {}),
        ...(document_ids?.length ? { document_ids: document_ids.join(',') } : {}),
        ...(unused ? { unused: true } : {}),
        ...(sort && sort !== 'newest' ? { sort } : {}),
      },
    });
    return response.data;
  }

  static async getApprovedQuestion(id: number): Promise<ApprovedQuestionDetail> {
    const response = await apiClient.get(`/api/v1/exams/approved-questions/${id}`);
    return response.data;
  }

  static async listDocumentsWithQuestions(): Promise<DocumentWithQuestions[]> {
    const response = await apiClient.get('/api/v1/exams/documents-with-questions');
    return response.data;
  }

  static async downloadExport(
    examId: number,
    format: string,
    includeSolutions = false
  ): Promise<{ skippedPositions: number[] }> {
    const params = new URLSearchParams();
    if (includeSolutions) params.set('include_solutions', 'true');
    const response = await apiClient.get(
      `/api/v1/exams/${examId}/export/${format}`,
      { params, responseType: 'blob' }
    );
    const contentDisposition = response.headers['content-disposition'];
    const filename = parseContentDispositionFilename(
      contentDisposition,
      `exam_export.${format}`
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    // ILIAS (TF-782) can silently drop unscoreable questions from the
    // export; the backend reports them via this header instead of a
    // shorter-than-expected 200 OK so the caller can warn the Dozent.
    const skippedHeader = response.headers['x-export-skipped-positions'];
    const skippedPositions = skippedHeader
      ? skippedHeader
          .split(',')
          .map((p: string) => parseInt(p, 10))
          .filter((p: number) => !Number.isNaN(p))
      : [];
    return { skippedPositions };
  }
}
